import {observer} from 'mobx-react-lite';

import {Link} from 'UI/components/layout/Text';

import type {AutoLinkNode, LinkNode} from '@nfq/payload-lexical-parser';
import type {WithOptionalChildren} from 'types/global';


/**
 * The `ComponentProps` interface defines the expected properties for the `RichtextLink` component.
 * It specifies the structure of data needed to render a semantic and accessible link element inside a lexical rich text context.
 * The core `node` property contains formatting and link-specific configuration such as URL and whether the link opens in a new tab.
 * The optional `testId` is used for assigning a consistent identifier to the rendered component for automated testing.
 */
interface ComponentProps {
    /**
     * The lexical node representing a link, including its formatting style and link-specific fields.
     * This node defines the link’s alignment, destination URL, and tab-opening behavior.
     */
    node: AutoLinkNode | LinkNode;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'RichtextLink'
     */
    testId?: string;
}

/**
 * The `RichtextLink` component renders a styled anchor element based on the configuration from a lexical rich text node.
 * It reads formatting and link data from the provided `node` prop, applying horizontal alignment and conditional behavior
 * for opening the link in a new tab if specified. This component is commonly used in rich text rendering pipelines
 * where structured content from a CMS needs to be converted to React elements.
 * The `children` are passed as the visible link content, which can include plain text or nested elements.
 * A `testId` is also supported for test automation purposes.
 *
 * @param props          The component props.
 * @param props.children Optional React children to be rendered inside the anchor element.
 * @param props.node     The lexical node containing formatting and URL information for the link.
 * @param props.testId   Optional string used to identify the component in tests (default: `'RichtextLink'`).
 *
 * @returns A JSX anchor (`<a>`) element with rich text formatting and navigation behavior.
 *
 * @example
 * ```tsx
 * <RichtextLink node={{ format: 'center', fields: { url: '/contact', newTab: false } }}>
 *   Contact Us
 * </RichtextLink>
 * ```
 */
const RichtextLink = observer(({children, node, testId = 'RichtextLink'}: WithOptionalChildren<ComponentProps>) => (
    <Link
        $align={node.format as 'center' | 'left' | 'right'}
        data-cy={testId}
        // @ts-expect-error
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, @typescript-eslint/no-unsafe-member-access
        href={node.fields?.url ?? node.fields?.doc?.value?.url}
        target={node.fields.newTab ? '_blank' : '_self'}
    >
        {children}
    </Link>
));

RichtextLink.displayName = 'RichtextLink';

export {RichtextLink};